import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import { useLang } from '../i18n';

const css = `
  .legal { padding: 6px 16px 32px; }
  .legal-hero { margin-bottom: 20px; padding: 16px 14px; border-radius: 12px; background: linear-gradient(135deg, rgba(129,135,246,.15), rgba(118,225,255,.06)); box-shadow: inset 0 0 0 1px rgba(118,225,255,.18); }
  .legal-hero-title { font-size: 17px; font-weight: 800; color: #fff; }
  .legal-hero-sub { font-size: 12px; color: #76e1ff; margin-top: 4px; }
  .legal-section { margin-top: 18px; }
  .legal-section-title { font-size: 13px; font-weight: 800; color: #fff; margin-bottom: 8px; letter-spacing: .2px; display: flex; align-items: center; gap: 7px; }
  .legal-section-title::before { content: ''; display: block; width: 3px; height: 14px; border-radius: 2px; background: linear-gradient(#8187f6, #76e1ff); flex: none; }
  .legal-p { font-size: 12.5px; color: #9b9ab0; line-height: 1.65; }
  .legal-p + .legal-p { margin-top: 8px; }
  .legal-ul { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
  .legal-ul li { font-size: 12.5px; color: #9b9ab0; padding-left: 14px; position: relative; line-height: 1.5; }
  .legal-ul li::before { content: '—'; position: absolute; left: 0; color: #76e1ff; }
  .legal-date { font-size: 11px; color: #5a5972; margin-top: 28px; text-align: center; }
`;

const CONTENT = {
  fr: {
    backTitle: 'Politique KYC',
    heroTitle: 'Politique KYC',
    heroSub: 'Vérification d\'identité — Mise à jour : janvier 2025',
    footer: 'Dernière mise à jour : janvier 2025',
    sections: [
      {
        title: '1. Qu\'est-ce que le KYC ?',
        body: [
          'La procédure KYC (Know Your Customer — Connaissance du Client) est une obligation légale imposée aux opérateurs de paris en ligne par les régulateurs financiers et de jeux.',
          'Elle vise à vérifier l\'identité de chaque utilisateur afin de prévenir la fraude, le blanchiment d\'argent et l\'accès des mineurs à la plateforme.',
        ],
      },
      {
        title: '2. Quand la vérification est-elle demandée ?',
        body: [
          'Une vérification d\'identité peut être requise lors de votre première demande de retrait, lorsque vos gains cumulés dépassent un certain seuil, ou à la demande de nos équipes de conformité.',
        ],
        list: [
          'Premier retrait supérieur à 150 €',
          'Cumul de dépôts dépassant 2 000 € sur 30 jours',
          'Suspicion d\'activité inhabituelle sur le compte',
        ],
      },
      {
        title: '3. Documents acceptés',
        body: ['Pour valider votre identité, vous devrez fournir un ou plusieurs des documents suivants :'],
        list: [
          'Carte nationale d\'identité (recto/verso)',
          'Passeport en cours de validité',
          'Permis de conduire',
          'Justificatif de domicile de moins de 3 mois',
        ],
      },
      {
        title: '4. Délais de traitement',
        body: [
          'Une fois les documents soumis, notre équipe procède à leur vérification dans un délai de 24 à 72 heures ouvrées. Vous serez notifié par email du résultat.',
        ],
      },
      {
        title: '5. Protection des données',
        body: [
          'Tous les documents transmis sont traités de manière confidentielle, stockés de façon sécurisée et utilisés uniquement à des fins de vérification réglementaire, conformément à notre Politique de confidentialité.',
        ],
      },
    ],
  },
  es: {
    backTitle: 'Política KYC',
    heroTitle: 'Política KYC',
    heroSub: 'Verificación de identidad — Actualización: enero de 2025',
    footer: 'Última actualización: enero de 2025',
    sections: [
      {
        title: '1. ¿Qué es el KYC?',
        body: [
          'El procedimiento KYC (Know Your Customer — Conozca a su Cliente) es una obligación legal impuesta a los operadores de apuestas en línea por los reguladores financieros y de juego.',
          'Tiene como objetivo verificar la identidad de cada usuario para prevenir el fraude, el blanqueo de capitales y el acceso de menores a la plataforma.',
        ],
      },
      {
        title: '2. ¿Cuándo se solicita la verificación?',
        body: [
          'Puede requerirse una verificación de identidad en su primera solicitud de retiro, cuando sus ganancias acumuladas superen un determinado umbral, o a petición de nuestros equipos de cumplimiento.',
        ],
        list: [
          'Primer retiro superior a 150 €',
          'Depósitos acumulados que superen los 2 000 € en 30 días',
          'Sospecha de actividad inusual en la cuenta',
        ],
      },
      {
        title: '3. Documentos aceptados',
        body: ['Para validar su identidad, deberá proporcionar uno o varios de los siguientes documentos:'],
        list: [
          'Documento nacional de identidad (anverso/reverso)',
          'Pasaporte en vigor',
          'Permiso de conducir',
          'Comprobante de domicilio de menos de 3 meses',
        ],
      },
      {
        title: '4. Plazos de procesamiento',
        body: [
          'Una vez enviados los documentos, nuestro equipo procede a su verificación en un plazo de 24 a 72 horas hábiles. Será notificado por correo electrónico del resultado.',
        ],
      },
      {
        title: '5. Protección de datos',
        body: [
          'Todos los documentos enviados se tratan de forma confidencial, se almacenan de manera segura y se utilizan únicamente con fines de verificación reglamentaria, conforme a nuestra Política de privacidad.',
        ],
      },
    ],
  },
  pt: {
    backTitle: 'Política KYC',
    heroTitle: 'Política KYC',
    heroSub: 'Verificação de identidade — Atualização: janeiro de 2025',
    footer: 'Última atualização: janeiro de 2025',
    sections: [
      {
        title: '1. O que é o KYC?',
        body: [
          'O procedimento KYC (Know Your Customer — Conheça o seu Cliente) é uma obrigação legal imposta aos operadores de apostas online pelos reguladores financeiros e de jogos.',
          'Ele visa verificar a identidade de cada usuário para prevenir fraudes, lavagem de dinheiro e o acesso de menores à plataforma.',
        ],
      },
      {
        title: '2. Quando a verificação é solicitada?',
        body: [
          'Uma verificação de identidade pode ser exigida na sua primeira solicitação de saque, quando seus ganhos acumulados ultrapassarem um determinado limite, ou a pedido das nossas equipes de conformidade.',
        ],
        list: [
          'Primeiro saque superior a 150 €',
          'Depósitos acumulados acima de 2 000 € em 30 dias',
          'Suspeita de atividade incomum na conta',
        ],
      },
      {
        title: '3. Documentos aceitos',
        body: ['Para validar sua identidade, você deverá fornecer um ou mais dos seguintes documentos:'],
        list: [
          'Documento de identidade (frente/verso)',
          'Passaporte válido',
          'Carteira de motorista',
          'Comprovante de residência com menos de 3 meses',
        ],
      },
      {
        title: '4. Prazos de processamento',
        body: [
          'Após o envio dos documentos, nossa equipe procede à verificação em um prazo de 24 a 72 horas úteis. Você será notificado por e-mail sobre o resultado.',
        ],
      },
      {
        title: '5. Proteção de dados',
        body: [
          'Todos os documentos enviados são tratados de forma confidencial, armazenados com segurança e utilizados exclusivamente para fins de verificação regulatória, conforme a nossa Política de privacidade.',
        ],
      },
    ],
  },
  en: {
    backTitle: 'KYC Policy',
    heroTitle: 'KYC Policy',
    heroSub: 'Identity verification — Updated: January 2025',
    footer: 'Last updated: January 2025',
    sections: [
      {
        title: '1. What is KYC?',
        body: [
          'The KYC (Know Your Customer) procedure is a legal requirement imposed on online betting operators by financial and gaming regulators.',
          'It aims to verify the identity of each user in order to prevent fraud, money laundering and access by minors to the platform.',
        ],
      },
      {
        title: '2. When is verification requested?',
        body: [
          'An identity verification may be required on your first withdrawal request, when your cumulative winnings exceed a certain threshold, or at the request of our compliance teams.',
        ],
        list: [
          'First withdrawal over €150',
          'Cumulative deposits exceeding €2,000 over 30 days',
          'Suspicion of unusual account activity',
        ],
      },
      {
        title: '3. Accepted documents',
        body: ['To validate your identity, you will need to provide one or more of the following documents:'],
        list: [
          'National identity card (front/back)',
          'Valid passport',
          'Driving licence',
          'Proof of address less than 3 months old',
        ],
      },
      {
        title: '4. Processing times',
        body: [
          'Once the documents are submitted, our team verifies them within 24 to 72 business hours. You will be notified of the result by email.',
        ],
      },
      {
        title: '5. Data protection',
        body: [
          'All submitted documents are treated confidentially, stored securely and used solely for regulatory verification purposes, in accordance with our Privacy Policy.',
        ],
      },
    ],
  },
  it: {
    backTitle: 'Politica KYC',
    heroTitle: 'Politica KYC',
    heroSub: 'Verifica dell\'identità — Aggiornamento: gennaio 2025',
    footer: 'Ultimo aggiornamento: gennaio 2025',
    sections: [
      {
        title: '1. Che cos\'è il KYC?',
        body: [
          'La procedura KYC (Know Your Customer — Conosci il tuo cliente) è un obbligo legale imposto agli operatori di scommesse online dalle autorità di regolamentazione finanziaria e di gioco.',
          'Ha lo scopo di verificare l\'identità di ogni utente per prevenire frodi, riciclaggio di denaro e l\'accesso dei minori alla piattaforma.',
        ],
      },
      {
        title: '2. Quando viene richiesta la verifica?',
        body: [
          'Una verifica dell\'identità può essere richiesta alla prima richiesta di prelievo, quando le vincite cumulative superano una certa soglia, o su richiesta dei nostri team di conformità.',
        ],
        list: [
          'Primo prelievo superiore a 150 €',
          'Depositi cumulativi superiori a 2.000 € in 30 giorni',
          'Sospetto di attività insolita sul conto',
        ],
      },
      {
        title: '3. Documenti accettati',
        body: ['Per convalidare la tua identità, dovrai fornire uno o più dei seguenti documenti:'],
        list: [
          'Carta d\'identità (fronte/retro)',
          'Passaporto in corso di validità',
          'Patente di guida',
          'Prova di residenza di meno di 3 mesi',
        ],
      },
      {
        title: '4. Tempi di elaborazione',
        body: [
          'Una volta inviati i documenti, il nostro team procede alla verifica entro 24-72 ore lavorative. Riceverai una notifica del risultato via email.',
        ],
      },
      {
        title: '5. Protezione dei dati',
        body: [
          'Tutti i documenti inviati sono trattati in modo riservato, conservati in modo sicuro e utilizzati esclusivamente a fini di verifica regolamentare, in conformità con la nostra Politica sulla privacy.',
        ],
      },
    ],
  },
  nl: {
    backTitle: 'KYC-beleid',
    heroTitle: 'KYC-beleid',
    heroSub: 'Identiteitsverificatie — Bijgewerkt: januari 2025',
    footer: 'Laatst bijgewerkt: januari 2025',
    sections: [
      {
        title: '1. Wat is KYC?',
        body: [
          'De KYC-procedure (Know Your Customer) is een wettelijke verplichting die aan online weddenschapaanbieders wordt opgelegd door financiële en kansspeltoezichthouders.',
          'Het doel is de identiteit van elke gebruiker te verifiëren om fraude, witwassen en toegang door minderjarigen tot het platform te voorkomen.',
        ],
      },
      {
        title: '2. Wanneer wordt verificatie gevraagd?',
        body: [
          'Een identiteitsverificatie kan vereist zijn bij je eerste opnameverzoek, wanneer je cumulatieve winst een bepaalde drempel overschrijdt, of op verzoek van onze complianceteams.',
        ],
        list: [
          'Eerste opname boven €150',
          'Cumulatieve stortingen boven €2.000 in 30 dagen',
          'Vermoeden van ongebruikelijke accountactiviteit',
        ],
      },
      {
        title: '3. Geaccepteerde documenten',
        body: ['Om je identiteit te valideren, moet je een of meer van de volgende documenten verstrekken:'],
        list: [
          'Identiteitskaart (voor-/achterkant)',
          'Geldig paspoort',
          'Rijbewijs',
          'Bewijs van adres van minder dan 3 maanden oud',
        ],
      },
      {
        title: '4. Verwerkingstijden',
        body: [
          'Zodra de documenten zijn ingediend, verifieert ons team ze binnen 24 tot 72 werkuren. Je wordt per e-mail op de hoogte gebracht van het resultaat.',
        ],
      },
      {
        title: '5. Gegevensbescherming',
        body: [
          'Alle ingediende documenten worden vertrouwelijk behandeld, veilig opgeslagen en uitsluitend gebruikt voor wettelijke verificatiedoeleinden, in overeenstemming met ons Privacybeleid.',
        ],
      },
    ],
  },
  tr: {
    backTitle: 'KYC Politikası',
    heroTitle: 'KYC Politikası',
    heroSub: 'Kimlik doğrulama — Güncelleme: Ocak 2025',
    footer: 'Son güncelleme: Ocak 2025',
    sections: [
      {
        title: '1. KYC nedir?',
        body: [
          'KYC (Know Your Customer — Müşterini Tanı) prosedürü, çevrimiçi bahis operatörlerine finansal ve oyun düzenleyicileri tarafından dayatılan yasal bir yükümlülüktür.',
          'Amacı, dolandırıcılığı, kara para aklamayı ve reşit olmayanların platforma erişimini önlemek için her kullanıcının kimliğini doğrulamaktır.',
        ],
      },
      {
        title: '2. Doğrulama ne zaman istenir?',
        body: [
          'İlk para çekme talebinizde, kümülatif kazançlarınız belirli bir eşiği aştığında veya uyum ekiplerimizin talebi üzerine kimlik doğrulaması istenebilir.',
        ],
        list: [
          '150 €\'nun üzerindeki ilk çekim',
          '30 günde 2.000 €\'yu aşan toplam yatırımlar',
          'Hesapta olağandışı etkinlik şüphesi',
        ],
      },
      {
        title: '3. Kabul edilen belgeler',
        body: ['Kimliğinizi doğrulamak için aşağıdaki belgelerden bir veya birkaçını sağlamanız gerekir:'],
        list: [
          'Kimlik kartı (ön/arka)',
          'Geçerli pasaport',
          'Sürücü belgesi',
          '3 aydan eski olmayan adres kanıtı',
        ],
      },
      {
        title: '4. İşlem süreleri',
        body: [
          'Belgeler gönderildikten sonra ekibimiz bunları 24 ila 72 iş saati içinde doğrular. Sonuç size e-posta ile bildirilecektir.',
        ],
      },
      {
        title: '5. Veri koruma',
        body: [
          'Gönderilen tüm belgeler gizli tutulur, güvenli bir şekilde saklanır ve yalnızca düzenleyici doğrulama amacıyla, Gizlilik Politikamıza uygun olarak kullanılır.',
        ],
      },
    ],
  },
  el: {
    backTitle: 'Πολιτική KYC',
    heroTitle: 'Πολιτική KYC',
    heroSub: 'Επαλήθευση ταυτότητας — Ενημέρωση: Ιανουάριος 2025',
    footer: 'Τελευταία ενημέρωση: Ιανουάριος 2025',
    sections: [
      {
        title: '1. Τι είναι το KYC;',
        body: [
          'Η διαδικασία KYC (Know Your Customer — Γνώρισε τον Πελάτη σου) είναι νομική υποχρέωση που επιβάλλεται στους διαδικτυακούς φορείς στοιχηματισμού από τις χρηματοοικονομικές και ρυθμιστικές αρχές τυχερών παιχνιδιών.',
          'Στόχος της είναι να επαληθεύσει την ταυτότητα κάθε χρήστη ώστε να αποτρέψει την απάτη, το ξέπλυμα χρήματος και την πρόσβαση ανηλίκων στην πλατφόρμα.',
        ],
      },
      {
        title: '2. Πότε ζητείται η επαλήθευση;',
        body: [
          'Επαλήθευση ταυτότητας μπορεί να απαιτηθεί κατά το πρώτο σας αίτημα ανάληψης, όταν τα συσσωρευμένα κέρδη σας υπερβαίνουν ένα ορισμένο όριο, ή κατόπιν αιτήματος των ομάδων συμμόρφωσής μας.',
        ],
        list: [
          'Πρώτη ανάληψη άνω των 150 €',
          'Συσσωρευμένες καταθέσεις άνω των 2.000 € σε 30 ημέρες',
          'Υποψία ασυνήθιστης δραστηριότητας στον λογαριασμό',
        ],
      },
      {
        title: '3. Αποδεκτά έγγραφα',
        body: ['Για την επικύρωση της ταυτότητάς σας, θα χρειαστεί να προσκομίσετε ένα ή περισσότερα από τα ακόλουθα έγγραφα:'],
        list: [
          'Δελτίο ταυτότητας (εμπρός/πίσω)',
          'Ισχύον διαβατήριο',
          'Άδεια οδήγησης',
          'Αποδεικτικό διεύθυνσης κάτω των 3 μηνών',
        ],
      },
      {
        title: '4. Χρόνοι επεξεργασίας',
        body: [
          'Μόλις υποβληθούν τα έγγραφα, η ομάδα μας τα επαληθεύει εντός 24 έως 72 εργάσιμων ωρών. Θα ειδοποιηθείτε για το αποτέλεσμα μέσω email.',
        ],
      },
      {
        title: '5. Προστασία δεδομένων',
        body: [
          'Όλα τα υποβληθέντα έγγραφα αντιμετωπίζονται εμπιστευτικά, αποθηκεύονται με ασφάλεια και χρησιμοποιούνται αποκλειστικά για σκοπούς κανονιστικής επαλήθευσης, σύμφωνα με την Πολιτική Απορρήτου μας.',
        ],
      },
    ],
  },
  ro: {
    backTitle: 'Politica KYC',
    heroTitle: 'Politica KYC',
    heroSub: 'Verificarea identității — Actualizare: ianuarie 2025',
    footer: 'Ultima actualizare: ianuarie 2025',
    sections: [
      {
        title: '1. Ce este KYC?',
        body: [
          'Procedura KYC (Know Your Customer — Cunoaște-ți Clientul) este o obligație legală impusă operatorilor de pariuri online de către autoritățile de reglementare financiară și a jocurilor de noroc.',
          'Aceasta urmărește verificarea identității fiecărui utilizator pentru a preveni frauda, spălarea banilor și accesul minorilor la platformă.',
        ],
      },
      {
        title: '2. Când este solicitată verificarea?',
        body: [
          'O verificare a identității poate fi necesară la prima cerere de retragere, când câștigurile cumulate depășesc un anumit prag, sau la solicitarea echipelor noastre de conformitate.',
        ],
        list: [
          'Prima retragere de peste 150 €',
          'Depozite cumulate de peste 2.000 € în 30 de zile',
          'Suspiciune de activitate neobișnuită în cont',
        ],
      },
      {
        title: '3. Documente acceptate',
        body: ['Pentru a vă valida identitatea, va trebui să furnizați unul sau mai multe dintre următoarele documente:'],
        list: [
          'Carte de identitate (față/verso)',
          'Pașaport valabil',
          'Permis de conducere',
          'Dovadă de domiciliu de mai puțin de 3 luni',
        ],
      },
      {
        title: '4. Termene de procesare',
        body: [
          'După trimiterea documentelor, echipa noastră le verifică într-un termen de 24 până la 72 de ore lucrătoare. Veți fi notificat prin email cu privire la rezultat.',
        ],
      },
      {
        title: '5. Protecția datelor',
        body: [
          'Toate documentele trimise sunt tratate confidențial, stocate în siguranță și utilizate exclusiv în scopuri de verificare reglementară, în conformitate cu Politica noastră de confidențialitate.',
        ],
      },
    ],
  },
};

export default function KycPolicy() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.fr;
  return (
    <PhoneShell activeTab="">
      <style>{css}</style>
      <BackHeader title={c.backTitle} />
      <div className="legal">
        <div className="legal-hero">
          <div className="legal-hero-title">{c.heroTitle}</div>
          <div className="legal-hero-sub">{c.heroSub}</div>
        </div>
        {c.sections.map((s) => (
          <div key={s.title} className="legal-section">
            <div className="legal-section-title">{s.title}</div>
            {s.body.map((p, i) => <p key={i} className="legal-p">{p}</p>)}
            {s.list && (
              <ul className="legal-ul">
                {s.list.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            )}
          </div>
        ))}
        <div className="legal-date">{c.footer}</div>
      </div>
    </PhoneShell>
  );
}
